import FlumpSprite from "./FlumpSprite";
import FlumpClip from "./FlumpClip";
import FlumpLibrary from "../FlumpLibrary";
import FlumpLayerSymbol from "../symbols/FlumpLayerSymbol";

/**
 * TODO - Add description.
 */
export default class FlumpLayer extends Phaser.Image
{
    /**
     * @param {Phaser.Game} game - A reference to the currently running game.
     * @param {FlumpLayerSymbol} layer - Layer json data.
     * @param {FlumpClip} movieClip - Parent movie clip.
     * @param {FlumpLibrary} flumpLibrary - FlumpLibrary.
     */
    constructor(game, layer, movieClip, flumpLibrary)
    {        
        super(game);
        
        /** @type {FlumpLibrary} */
        this.flumpLibrary = flumpLibrary;
        
        /** @type {string} */
        this.currentSprite = null;
        /** @type {number} */
        this.skewX = 0;
        /** @type {number} */
        this.skewY = 0;

        this.name = "FlumpLayer";
        
        this.setupLayer(layer, movieClip);
    }
    
    /**
     * Cleans up the FlumpLayer.
     * @param {boolean} [destroyChildren=true] Defaulfs to true
     */
    destroy(destroyChildren)
    {
        this.flumpLibrary = null;  
        this.movieClip = null;
        this.layer = null;
        super.destroy(destroyChildren);
    }
    
    /**
     * Setup the layer for the current animation.
     * @param {FlumpLayerSymbol} layer - Layer json data.
     * @param {FlumpClip} movieClip - Parent movie clip.
     */
    setupLayer(layer, movieClip)
    {
        /** @type {FlumpLayerSymbol} */
        this.layer = layer;
        /** @type {number} */
        this.keyframes = this.layer.keyframes;
        /** @type {number} */
        this.finalFrame = this.keyframes.length - 1;
        
        /** @type {FlumpClip} */
        this.movieClip = movieClip;
        /** @type {number} */
        this.frameRate = this.movieClip.frameRate;
        /** @type {number} */
        this.duration = this.movieClip.duration;
        /** @type {number} */
        this.playbackSpeed = 1;
        this._paused = false;
        /** @type {boolean} */
        this.visible = true;
        /** @type {boolean} */
        this.enabled = true;
        this.setActive(true);
        
        /** @internal Current frame*/
        this.currentFrame = 0;
        /** @internal Current keyframe index*/
        this.currentIndex = 0;
        /** @internal Current keyframe*/
        this.currentKeyFrame = this.keyframes[this.currentIndex];
        /** @internal Current keyframe duration*/
        this.currentDuration = 0;
        /** @internal Current time in current keyframe*/
        this.currentTime = 0;

        this.nextKeyframe();
        this.updateChildTransform(this.currentKeyFrame.x, this.currentKeyFrame.y, this.currentKeyFrame.pivotX, this.currentKeyFrame.pivotY, this.currentKeyFrame.skewX, this.currentKeyFrame.skewY, this.currentKeyFrame.scaleX, this.currentKeyFrame.scaleY, this.currentKeyFrame.alpha);
    }
    
    /**
     * Change the texture for this sprite.
     * @param {string} symbolName - New sprite.
     */
    changeFrame(symbolName)
    {
        if(this.currentSprite == symbolName)
        {
            return;
        }
        
        this.currentSprite = symbolName;
        
        if(this.currentSprite != null)
        {
            this.loadTexture(this.game.cache.getBitmapData(this.currentSprite));
            this.visible = true;
        }
        else
        {
            this.visible = false;
        }
    }
    
    /**
     * Go to new frame.
     * @param {number} newFrame - New frame to move to.
     * @param {number} playbackSpeed - Current playback speed.
     */
    goto(newFrame, playbackSpeed, looping)
    {      
        this.playbackSpeed = playbackSpeed;
        
        if (this.currentFrame == newFrame) 
        {
            return; // No change
        }
        
        if (newFrame < this.currentFrame) {
            this.currentIndex = 0;
            this.currentDuration = 0;
            this.currentFrame = 0;
            this.currentTime -= this.currentKeyFrame.duration;
            this.nextKeyframe();
        }

        this.composeFrame(newFrame, looping);
    }
    
    /** @internal */
    composeFrame(frame, looping)
    {
        this.currentFrame = frame;
        
        //TODO: Make this work
        if(frame >= this.layer.frames) 
        {
            this.visible = false;
            this.currentIndex = this.finalFrame;
            this.keyframeUpdate = true;
            return;
        }

        while(true)
        {
            if(this.currentDuration <= this.currentFrame) 
            {
                this.currentIndex++;
                this.nextKeyframe();
            }
            else
            {
                break;
            }
        }

        var kf = this.keyframes[this.currentIndex];
        var visible = kf.visible && kf.symbolName != null;
        this.visible = visible;
        if (!visible) 
        {
            return;
        }

        var x = kf.x;
        var y = kf.y;
        var scaleX = kf.scaleX;
        var scaleY = kf.scaleY;
        var skewX = kf.skewX;
        var skewY = kf.skewY;
        var alpha = kf.alpha;

        if (kf.tweened && this.currentIndex < this.finalFrame && this.keyframes[this.currentIndex + 1].symbolName != null) 
        {
            var interp = (this.currentFrame-kf.index) / kf.duration;
            var ease = kf.ease;
            if (ease != 0) {
                var t;
                if (ease < 0) {
                    // Ease in
                    var inv = 1 - interp;
                    t = 1 - inv*inv;
                    ease = -ease;
                } else {
                    // Ease out
                    t = interp*interp;
                }
                interp = ease*t + (1 - ease)*interp;
            }

            var nextKf = this.keyframes[this.currentIndex + 1];
            x += (nextKf.x-x) * interp;
            y += (nextKf.y-y) * interp;
            scaleX += (nextKf.scaleX-scaleX) * interp;
            scaleY += (nextKf.scaleY-scaleY) * interp;
            skewX += (nextKf.skewX-skewX) * interp;
            skewY += (nextKf.skewY-skewY) * interp;
            alpha += (nextKf.alpha-alpha) * interp;
        }

        this.updateChildTransform(x, y, this.currentKeyFrame.pivotX, this.currentKeyFrame.pivotY, skewX, skewY, scaleX, scaleY, alpha);
    }    
    
    /** @internal */
    nextKeyframe()
    {        
        if(this.currentIndex >= this.keyframes.length)
        {
            this.currentIndex = 0;
            this.currentDuration = 0;
            this.currentFrame = 0;
            this.currentTime -= this.currentKeyFrame.duration;
        }
        else
        {   
            this.currentTime = 0;
        }
        
        this.currentKeyFrame = this.keyframes[this.currentIndex];
        this.currentDuration += this.currentKeyFrame.duration;

        if(this.currentKeyFrame.symbolName != null)
        {
            this.visible = true;
            this.changeFrame(this.currentKeyFrame.symbolName);
        }
        else
        {
            this.visible = false;
        }
    }
    
    /** @internal */
    updateChildTransform(newX, newY, newPivotX, newPivotY, newSkewX, newSkewY, newScaleX, newScaleY, newAlpha)
    {               
        if(this.visible)
        {         
            this.position.set(newX, newY);
            this.pivot.set(newPivotX, newPivotY);
            this.skewX = newSkewX;
            this.skewY = newSkewY;
            this.scale.set(newScaleX, newScaleY);
            this.alpha = newAlpha;
        }
    }
    
    /** @param {boolean} value - Enable/Disable layer. */
    setActive(value)
    {
        this.visible = value;
        this.enabled = value;
    }
    
    /** @internal */
    checkSpriteExist(spriteName)
    {
        return this.game.cache.checkBitmapDataKey(spriteName);
    }

    setChildVisible(child, value)
    {
        if(child != null)
        {
            child.visible = value;
        }
    }
    
    /** @type {boolean} */
    get paused()
    {
        return this._paused;
    }
    
    /** @type {boolean} */
    set paused(val)
    {
        this._paused = val;
    }

    /** @internal */
    updateTransform(parent)
    {        
        if (!parent && !this.parent && !this.game)
        {
            return;
        }

        var p = this.parent;

        if (parent)
        {
            p = parent;
        }
        else if (!this.parent)
        {
            p = this.game.world;
        }

        // create some matrix refs for easy access
        var pt = p.worldTransform;
        var wt = this.worldTransform;

        // temporary matrix variables
        var a, b, c, d, tx, ty;

        // so if rotation is between 0 then we can simplify the multiplication process..
        if (this.rotation % PIXI.PI_2 || this.skewX || this.skewY)
        {
            // check to see if the rotation is the same as the previous render. This means we only need to use sin and cos when rotation actually changes
            // if (this.rotation !== this.rotationCache)
            // {
            //     this.rotationCache = this.rotation;
            //     this._sr = Math.sin(this.rotation);
            //     this._cr = Math.cos(this.rotation);
            // }

            // get the matrix values of the displayobject based on its transform properties..
            a  =  this.scale.x * Math.cos(this.rotation + this.skewY);        
            b  =  this.scale.x * Math.sin(this.rotation + this.skewY);
            c  =  this.scale.y * Math.sin(-this.rotation - this.skewX);        
            d  =  this.scale.y * Math.cos(this.rotation + this.skewX);
            tx =  this.position.x;
            ty =  this.position.y;
            
            // check for pivot.. not often used so geared towards that fact!
            if (this.pivot.x || this.pivot.y)
            {
                tx -= this.pivot.x * a + this.pivot.y * c;
                ty -= this.pivot.x * b + this.pivot.y * d;
            }

            // concat the parent matrix with the objects transform.
            wt.a  = a  * pt.a + b  * pt.c;
            wt.b  = a  * pt.b + b  * pt.d;
            wt.c  = c  * pt.a + d  * pt.c;
            wt.d  = c  * pt.b + d  * pt.d;
            wt.tx = tx * pt.a + ty * pt.c + pt.tx;
            wt.ty = tx * pt.b + ty * pt.d + pt.ty;
        }
        else
        {
            // lets do the fast version as we know there is no rotation..
            a  = this.scale.x;
            d  = this.scale.y;

            tx = this.position.x - this.pivot.x * a;
            ty = this.position.y - this.pivot.y * d;

            wt.a  = a  * pt.a;
            wt.b  = a  * pt.b;
            wt.c  = d  * pt.c;
            wt.d  = d  * pt.d;
            wt.tx = tx * pt.a + ty * pt.c + pt.tx;
            wt.ty = tx * pt.b + ty * pt.d + pt.ty;
        }

        // multiply the alphas..
        /** @ignore */
        this.worldAlpha = this.alpha * p.worldAlpha;

        this.worldPosition.set(wt.tx, wt.ty);
        this.worldScale.set(Math.sqrt(wt.a * wt.a + wt.b * wt.b), Math.sqrt(wt.c * wt.c + wt.d * wt.d));
        /** @ignore */
        this.worldRotation = Math.atan2(-wt.c, wt.d);

        // reset the bounds each time this is called!
        this._currentBounds = null;

        //  Custom callback?
        if (this.transformCallback)
        {
            this.transformCallback.call(this.transformCallbackContext, wt, pt);
        }
    }
}